// Collector's Vault design tokens — the single source of truth for the
// FightDecks visual system. The MUI theme (src/theme.js) is built from these;
// components should pull from the theme (or import tokens directly) rather
// than hardcoding colors, fonts, or radii.

export const colors = {
  // Surfaces — depth comes from background steps + 1px borders, never shadows
  void: '#0C0D0F', // app background (near-black graphite)
  surface: '#121315', // sidebar & top-bar shell surface
  panel: '#16181C', // panels, cards
  panelRaised: '#1F2228', // raised / hover surfaces
  surfaceHigh: '#292A2C', // active nav / highest containers
  border: '#2A2E36', // borders, dividers, progress tracks (steel gray)
  // Input/control outlines need ≥3:1 vs panel/void (WCAG non-text contrast)
  borderInput: '#5A6270',

  // Text
  textPrimary: '#EDEAE3', // warm off-white
  textSecondary: '#9E9992', // warm neutral gray — muted labels, timestamps, descriptions
  textSteel: '#8A9099', // cool steel — reserved for de-emphasized data

  // Accents — championship gold is a guest: key stats, primary CTAs, completion bars only
  brass: '#D4AF37', // championship-belt gold
  brassBright: '#E9C858', // polished gold hover
  brassDark: '#B18E1F', // darker gradient stop
  brassEdge: '#8C6F16', // pressed bottom edge on gold CTAs
  steelBlue: '#4A8DB7', // secondary / info states and tags
  salmon: '#4A8DB7', // repointed off warm-pink → steel-blue: active nav rule, highlights
  green: '#22C55E', // ONLY complete / secured states
  coral: '#4A8DB7', // repointed off warm-pink → steel-blue: "new acquisition" tags, MUI secondary
  red: '#B82D24', // fills / borders only — too dark for body text on panels
  redFg: '#E85D55', // error/status text & icons on dark panels (~5:1 on panel)
  amber: '#C97F2E', // warnings / pending states — burnished, never neon orange
  violet: '#8E6FB8', // reserved for special role badges (founder/moderator tiers)
  violetBright: '#A88BD0', // polished violet hover / seal highlight
  violetDark: '#6E549A', // darker violet gradient stop
  violetEdge: '#55407A', // pressed bottom edge on violet seals
};

// Championship-gold gradients: vertical for numerals & CTAs, horizontal for bars
export const gradients = {
  brassVertical: `linear-gradient(180deg, ${colors.brass} 0%, ${colors.brassDark} 100%)`,
  brassVerticalBright: `linear-gradient(180deg, ${colors.brassBright} 0%, ${colors.brass} 100%)`,
  brassHorizontal: `linear-gradient(90deg, ${colors.brass} 0%, ${colors.brassDark} 100%)`,
};

export const fonts = {
  // Brand: wordmark, hero nameplates, monogram — same boxing-heritage face as
  // display so the brand voice is one font. Single weight, fontWeight 400 only.
  brand: '"Shapiro 85 Super Heavy Wide", "Arial Black", Arial, sans-serif',
  // Display: headings, page titles, button labels (UPPERCASE). Boxing-heritage
  // heavy-wide face, single weight — always fontWeight 400, never faux-bold.
  display: '"Shapiro 85 Super Heavy Wide", "Arial Black", Arial, sans-serif',
  // Impact: the forward-leaning cut of the same family — hero CTAs
  // (size="large" buttons) and big-moment headlines only.
  impact: '"Shapiro 96", "Arial Black", Arial, sans-serif',
  // Nav: sidebar menu items keep the condensed face the user approved.
  nav: '"Oswald", "Arial Narrow", Arial, sans-serif',
  // Numerals & data values: big stat numbers, counts, serials
  numeric: '"Oswald", "Arial Narrow", Arial, sans-serif',
  // Body & labels
  body: '"Manrope", "Helvetica Neue", Arial, sans-serif',
};

// Tabular figures so numbers align in stats, tables, and feeds
export const numeric = {
  fontFeatureSettings: "'tnum'",
};

// Hard edges everywhere — no rounded cards, no pills
export const radius = 2;

// TopBar / dashboard header chrome — equal hit targets + glyph scale
export const chrome = {
  hit: 40, // IconButton / avatar frame (px)
  icon: 22, // Message / Bell / Admin / menu glyph (px)
  badge: 20, // unread count overlay min size (px)
};

// Clubhouse type floors — kill micro-type; short caps badges may sit at badgeFloor
export const typeScale = {
  body: '1rem', // readable copy
  bodySm: '0.9rem', // secondary readable
  label: '0.85rem', // meta / form labels / Oswald caps
  meta: '0.8125rem', // timestamps, helper rows
  badge: '0.75rem', // short pill / chip caps (floor)
  badgeFloor: '0.7rem', // absolute floor — dense overlays only
};

// 8px spacing base (matches the MUI default unit)
export const spacingUnit = 8;

// Trading cards render at true trading-card proportions
export const cardAspectRatio = '2.5 / 3.5';

// Card frame treatments
// UT-chrome: rollback — consumers of cardFrames.parallels / missingOpacity can
// drop those keys; grid chrome falls back to Card.css defaults.
export const cardFrames = {
  owned: `1px solid ${colors.border}`, // owned = full color + thin steel frame
  chase: `1px solid ${colors.brass}`, // chase / rare = brass frame
  missingOpacity: 0.2, // missing / ghost binder slots
  // Parallel-tier border colors (wired via data-parallel-tier on .custom-card)
  parallels: {
    base: 'rgba(237, 234, 227, 0.28)',
    silver: 'rgba(192, 192, 192, 0.75)',
    gold: 'rgba(212, 175, 55, 0.85)',
    black: 'rgba(90, 90, 95, 0.9)',
    red: 'rgba(184, 45, 36, 0.75)',
    blue: 'rgba(74, 141, 183, 0.75)',
    green: 'rgba(34, 197, 94, 0.7)',
    purple: 'rgba(142, 111, 184, 0.75)',
    orange: 'rgba(201, 127, 46, 0.75)',
    numbered: 'rgba(212, 175, 55, 0.95)',
  },
};

const tokens = {
  colors,
  gradients,
  fonts,
  numeric,
  radius,
  chrome,
  typeScale,
  spacingUnit,
  cardAspectRatio,
  cardFrames,
};

export default tokens;
